import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Star, RotateCcw, Volume2 } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const MatchingGame = ({ pairs, onComplete, childId, title = "Match the pairs!" }) => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    initializeGame();
  }, [pairs]);

  const initializeGame = () => {
    // Create card pairs
    const cardPairs = pairs.flatMap((pair, index) => [
      { id: `${index}-a`, pairId: index, content: pair.item1, type: 'a' },
      { id: `${index}-b`, pairId: index, content: pair.item2, type: 'b' }
    ]);
    
    // Shuffle cards
    const shuffled = cardPairs.sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setCompleted(false);
  };

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  const handleCardClick = (card) => {
    if (isChecking || flipped.length >= 2 || flipped.includes(card.id) || matched.includes(card.pairId)) {
      return;
    }

    speakText(card.content);
    const newFlipped = [...flipped, card.id];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      setIsChecking(true);

      const [firstId, secondId] = newFlipped;
      const firstCard = cards.find(c => c.id === firstId);
      const secondCard = cards.find(c => c.id === secondId);

      if (firstCard.pairId === secondCard.pairId && firstCard.type !== secondCard.type) {
        // Match found!
        setTimeout(() => {
          setMatched(m => [...m, firstCard.pairId]);
          setFlipped([]);
          setIsChecking(false);
          toast.success('Match found!', { duration: 1000 });

          // Check if game complete
          if (matched.length + 1 === pairs.length) {
            setCompleted(true);
            const score = Math.max(100 - (moves * 5), 50);
            if (onComplete) {
              onComplete(score, moves + 1);
            }
          }
        }, 500);
      } else {
        // No match
        setTimeout(() => {
          setFlipped([]);
          setIsChecking(false);
        }, 1000);
      }
    }
  };

  const handleReset = () => {
    initializeGame();
  };

  const getCardBg = (card) => {
    if (matched.includes(card.pairId)) {
      return 'bg-green-100 border-green-400';
    }
    if (flipped.includes(card.id)) {
      return 'bg-purple-100 border-purple-400';
    }
    return 'bg-gradient-to-br from-purple-400 to-pink-400 border-purple-500';
  };

  if (completed) {
    const score = Math.max(100 - (moves * 5), 50);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-purple-800 mb-2">All Matched!</h2>
        <p className="text-xl text-purple-600 mb-2">Completed in {moves} moves</p>
        <p className="text-lg text-slate-500 mb-6">Score: {score}%</p>
        <Button onClick={handleReset} variant="outline" className="gap-2">
          <RotateCcw className="w-4 h-4" /> Play Again
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matching-game">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-fredoka text-xl font-bold text-purple-800">{title}</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">Moves: {moves}</span>
          <span className="text-sm text-slate-500">Matched: {matched.length}/{pairs.length}</span>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Game Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <AnimatePresence>
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id) || matched.includes(card.pairId);
            
            return (
              <motion.div
                key={card.id}
                layout
                initial={{ rotateY: 0 }}
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                style={{ perspective: 1000 }}
              >
                <Card
                  className={`cursor-pointer transition-all h-24 sm:h-28 ${getCardBg(card)} ${
                    matched.includes(card.pairId) ? 'opacity-70' : 'hover:shadow-lg'
                  }`}
                  onClick={() => handleCardClick(card)}
                  data-testid={`card-${card.id}`}
                >
                  <CardContent className="p-2 h-full flex items-center justify-center">
                    {isFlipped ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center"
                        style={{ transform: 'rotateY(180deg)' }}
                      >
                        {matched.includes(card.pairId) && (
                          <CheckCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                        )}
                        <span className="font-bold text-sm sm:text-base text-slate-800">
                          {card.content}
                        </span>
                      </motion.div>
                    ) : (
                      <span className="text-3xl">❓</span>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Instructions */}
      <div className="text-center text-sm text-slate-500">
        <p>Click cards to flip them. Find all matching pairs!</p>
      </div>
    </div>
  );
};

export default MatchingGame;
